import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Notifications from "@/pages/Notifications";
import UpgradeDialog from "@/components/UpgradeDialog";
import InstallPrompt from "@/components/InstallPrompt";
import OfflineBanner from "@/components/OfflineBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

function Splash() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6 px-6" data-testid="app-loading">
      <img src="/DietFlix-logo.webp" alt="DietFlix" className="h-14 sm:h-16 w-auto animate-[fadeInUp_0.5s_ease-out]" />
      <p className="text-muted-foreground text-base font-medium animate-[fadeInUp_0.5s_ease-out_0.1s_both]">La tua dieta su misura</p>
      <div className="mt-2 h-9 w-9 rounded-full border-[3px] border-primary/20 border-t-primary animate-spin" aria-label="Caricamento" />
    </div>
  );
}

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return <Splash />;
  if (!user) return <Login />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  return <Navigate to={user.onboarding_completed ? "/dashboard" : "/onboarding"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute role="user"><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute role="user"><Dashboard /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute role="user"><Notifications /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute role="user"><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute role="user"><PaymentCancel /></ProtectedRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
        </BrowserRouter>
        <UpgradeDialog />
        <InstallPrompt />
        <OfflineBanner />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
